import { type FC, useEffect, useRef } from 'react';
import { SearchInput } from '@interstate/components/SearchInput';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { Card } from '@interstate/components/Card';
import type { BusinessEntity } from '@makemydeal/dr-dash-store/reducers/editCustomerReducer';
import { BusinessSearchContainer, ResultsHeaderContainer } from './BusinessSearch.styles';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import { BuildingOfficeIcon } from '@interstate/components/Icons';
import { Spinner } from '@interstate/components/Spinner';

export interface BusinessSearchUIProps {
    searchValue: string;
    onSearchValueChange: (value: string) => void;
    onSearchClick: () => void;
    onClear: () => void;
    results: BusinessEntity[];
    loading: boolean;
    error: string | null;
    hasSearched: boolean;
    selectedBusinessEntityId: string | null;
    onResultSelect: (entity: BusinessEntity) => void;
    handleNewBusiness: () => void;
}

const BusinessSearchUI: FC<BusinessSearchUIProps> = ({
    searchValue,
    onSearchValueChange,
    onSearchClick,
    onClear,
    results,
    loading,
    error,
    hasSearched,
    selectedBusinessEntityId,
    onResultSelect,
    handleNewBusiness
}) => {
    const isResultsAvailable = Boolean(results.length);
    const searchInputContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (searchInputContainerRef.current) {
            const input = searchInputContainerRef.current.querySelector('input');
            input?.focus();
        }
    }, []);

    return (
        <BusinessSearchContainer data-testid="business-search-ui">
            <Typography variant="body-md" sx={{ display: 'block', marginBottom: '1rem' }}>
                Search for an existing business first to copy its record. If no match is found, you can create a new one.
                {'\n'}
                This converts the deal to a business customer. The buyer becomes a signer, personal fields are replaced with
                business ones, and sensitive personal data is permanently removed.
            </Typography>
            <Typography variant="label-md" sx={{ display: 'block', marginBottom: '0.25rem', color: '#6d6d6d' }}>
                Search for Business
            </Typography>
            <div ref={searchInputContainerRef}>
                <SearchInput
                    id="business-search-input"
                    data-testid="business-search-input"
                    name="businessSearch"
                    placeholder="Enter business name..."
                    inputValue={searchValue}
                    onInputChange={(e: any) => onSearchValueChange(e.target.value ?? '')}
                    onSearchClick={() => onSearchClick()}
                    onClearCallback={() => onClear()}
                    style="primary"
                    size="small"
                    sx={{ marginBottom: '0.5rem' }}
                />
            </div>

            {loading && (
                <LoadingSectionParent
                    data-testid="business-search-loading"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        height: '10rem',
                        gap: '1rem'
                    }}
                >
                    <Spinner size={60} />
                    <Typography variant="body-md">Loading Results</Typography>
                </LoadingSectionParent>
            )}

            {error && (
                <Alert type="error" data-testid="business-search-error">
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <div data-testid="business-search-results">
                    {hasSearched && (
                        <ResultsHeaderContainer>
                            <Typography variant="h6">
                                {!isResultsAvailable ? 'No Results Found' : `Results: ${results.length}`}
                            </Typography>
                            <Button
                                data-testid="business-search-add-new-button"
                                buttonStyle="tertiary"
                                size="small"
                                startIcon={<BuildingOfficeIcon />}
                                onClick={handleNewBusiness}
                            >
                                Add New Business
                            </Button>
                        </ResultsHeaderContainer>
                    )}
                    {isResultsAvailable &&
                        results.map((entity) => {
                            const isSelected = selectedBusinessEntityId === entity.businessEntityId;
                            const address = entity.primaryAddress;
                            const formattedAddress = address
                                ? [
                                      address.street1,
                                      address.street2,
                                      address.city,
                                      address.stateOrProvinceAbbreviation,
                                      address.postalCode
                                  ]
                                      .filter(Boolean)
                                      .join(', ')
                                : '';
                            return (
                                <Card
                                    key={entity.businessEntityId}
                                    data-testid={`business-search-result-${entity.businessEntityId}`}
                                    primaryAction={() => onResultSelect(entity)}
                                    padding="1rem"
                                    sx={{
                                        marginBottom: '0.75rem',
                                        border: isSelected ? '0.125rem solid #003468' : '0.125rem solid #d1d5db',
                                        backgroundColor: isSelected ? '#eef2fb' : 'white',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: isSelected ? '#eef2fb' : '#f5f5f5'
                                        }
                                    }}
                                    content={
                                        <>
                                            <Typography variant="strong-md">{entity.businessName}</Typography>
                                            {formattedAddress && (
                                                <Typography variant="body-sm" sx={{ color: '#6b7280', marginTop: '0.25rem' }}>
                                                    {formattedAddress}
                                                </Typography>
                                            )}
                                        </>
                                    }
                                />
                            );
                        })}
                </div>
            )}
        </BusinessSearchContainer>
    );
};

export default BusinessSearchUI;
